import { useMount, useSetState, useUpdateEffect } from "ahooks";
import TopFilterBase from "../../../../commonComponents/listBase/TopFilterBase";
import InputLabel from "../../../../../base/basicComponents/inputLabel";
import NormalSelect from "../../../../../base/basicComponents/select";
import Buttons from "../../../../../base/basicComponents/button";
import toDoListService from "../../../../../service/toDoListService/index";
import styles from "./index.module.scss";
import "../../../../../assets/css/style.css";

/**
 * TopFilter Component
 *
 * This component manages the filtering interface for the ToDo list, handling multiple filter categories:
 * - Account Code
 * - Action Required (only visible when tab === 0)
 * - Action Taken (only visible when tab === 1)
 * - Process Category
 *
 * State Management:
 * - filterOptionList: Stores the available options for each filter category
 * - selectOptions: Tracks the currently selected values for each filter category
 *
 * Filter Behavior:
 * 1. Each filter category maintains two states:
 *    - Available options (filterOptionList)
 *    - Selected values (selectOptions)
 * 2. When options are selected:
 *    - Selected items appear first in the dropdown
 *    - Remaining items are sorted alphabetically
 * 3. The filter can be:
 *    - Applied using the Filter button
 *    - Reset using the Clear button
 *    - Select All/Deselect All options are available when options exist
 */
export default function TopFilter(props: any) {
  const { handleOptionList, sortList, handleChange, handleDeSelect, handleFilterOption } =
    TopFilterBase();

  /**
   * State Management for Filter Options and Selections
   * filterOptionList: Available options for each filter category
   * selectOptions: Currently selected values for each category
   */
  const [filterOptionList, setFilterOptionList] = useSetState({
    accountAbbr: [],
    actionRequired: [],
    actionTaken: [],
    processCategory: [],
  });

  const [selectOptions, setSelectOptions] = useSetState({
    accountAbbr: [],
    actionRequired: [],
    actionTaken: [],
    processCategory: [],
  });

  const onSuccess = (response: any, type: any) => {
    let option;
    switch (type) {
      case "accountAbbr":
        option = handleOptionList(response, true);
        const filterOptionPara = {
          accountAbbr: option,
        };
        const selectOptionsPara = {
          accountAbbr: [],
        };
        props.handleFilterClick(filterOptionPara, selectOptionsPara);
        // @ts-ignore
        setFilterOptionList({ accountAbbr: option });
        break;
      case "actionRequired":
        option = handleOptionList(response, false);
        setFilterOptionList({
          // @ts-ignore
          actionRequired: option,
        });
        break;
      case "actionTaken":
        option = handleOptionList(response, false);
        // @ts-ignore
        setFilterOptionList({ actionTaken: option });
        break;
      case "processCategory":
        option = handleOptionList(response, false);
        // @ts-ignore
        setFilterOptionList({ processCategory: option });
        break;
      default:
        break;
    }
  };

  /* callback about when call api error */
  const onError = (error: any) => {
    console.log(error);
  };

  const handleFilterClick = () => {
    props.handleFilterClick(filterOptionList, selectOptions);
  };

  const handleClearClick = () => {
    props.setLoading(true);
    const defaultOption = {
      accountAbbr: [],
      actionRequired: [],
      actionTaken: [],
      processCategory: [],
    };
    setSelectOptions(defaultOption);
    getAccountAbbr();
    getActionRequired();
    getActionTaken();
    getProcessCategory();
  };

  const handleSelectAll = (filterName: any) => {
    // @ts-ignore
    const optionList = filterOptionList[filterName];
    const allOptionsValue = optionList.map((item: any) => item.value);

    // @ts-ignore
    setSelectOptions({
      [filterName]: allOptionsValue,
    });
    // @ts-ignore
    setFilterOptionList({
      [filterName]: [...handleChange(optionList, allOptionsValue)],
    });
  };

  const handleDeselectAll = (filterName: any) => {
    // @ts-ignore
    const optionList = filterOptionList[filterName];

    // @ts-ignore
    setSelectOptions({
      [filterName]: [],
    });
    // @ts-ignore
    setFilterOptionList({
      [filterName]: [...optionList.sort((s: any, t: any) => sortList(s, t))],
    });
  };

  const getAccountAbbr = () => {
    if (props.isOpex) {
      toDoListService.getAllAccountAbbreviations(2, onSuccess, onError, "accountAbbr");
    } else {
      toDoListService.getAccountAbbrList(onSuccess, onError, "accountAbbr");
    }
  };

  const getActionRequired = () => {
    toDoListService.getActionRequiredDDL(onSuccess, onError, "actionRequired");
  };

  const getActionTaken = () => {
    toDoListService.getActionTakenDDL(onSuccess, onError, "actionTaken");
  };

  const getProcessCategory = () => {
    toDoListService.getProcessCategoryDDL(onSuccess, onError, "processCategory");
  };

  /* function about when page init get filter selects option list */
  useMount(() => {
    getAccountAbbr();
    getActionRequired();
    getActionTaken();
    getProcessCategory();
  });

  useUpdateEffect(() => {
    setSelectOptions({
      accountAbbr: [],
      actionRequired: [],
      actionTaken: [],
      processCategory: [],
    });
    const filterOptionPara = {
      accountAbbr: filterOptionList.accountAbbr,
    };
    const selectOptionsPara = {
      accountAbbr: [],
    };
    setFilterOptionList({
      accountAbbr: filterOptionList.accountAbbr.sort(sortList),
      actionRequired: filterOptionList.actionRequired.sort(sortList),
      actionTaken: filterOptionList.actionTaken.sort(sortList),
      processCategory: filterOptionList.processCategory.sort(sortList),
    });
    props.handleFilterClick(filterOptionPara, selectOptionsPara);
  }, [props.tab]);

  return (
    <div className={styles.topFilterContainer}>
      <div className={styles.topFilterSelects}>
        <div className={styles.topFilterSelectItem}>
          <InputLabel text="Account Code" className={`${styles.topFilterSelectLabel} fs-14`} />
          <NormalSelect
            value={selectOptions.accountAbbr}
            optionList={filterOptionList.accountAbbr}
            className="topFilterSelectInput"
            mode="multiple"
            showArrow={true}
            key={props.tab}
            maxTagCount="responsive"
            testid="account-code-select"
            handleChange={(e: any) => {
              const optionList = filterOptionList.accountAbbr;
              setSelectOptions({
                accountAbbr: e,
              });
              setFilterOptionList({
                // @ts-ignore
                accountAbbr: [
                  ...handleChange(optionList, e),
                  ...optionList.sort((s, t) => sortList(s, t)),
                ],
              });
            }}
            handleDeSelect={(e: any) => {
              const selectList = [...selectOptions.accountAbbr];
              setSelectOptions({
                accountAbbr: handleDeSelect(e, selectList),
              });
            }}
            handleFilterOption={(input: any, option: any) => handleFilterOption(input, option)}
            isNeedSelectAll={filterOptionList.accountAbbr.length > 0}
            handleSelectAll={() => handleSelectAll("accountAbbr")}
            handleDeselectAll={() => handleDeselectAll("accountAbbr")}
          />
        </div>
        {props.tab === 0 && (
          <div className={styles.topFilterSelectItem}>
            <InputLabel text="Action Required" className={`${styles.topFilterSelectLabel} fs-14`} />
            <NormalSelect
              value={selectOptions.actionRequired}
              optionList={filterOptionList.actionRequired}
              className="topFilterSelectInput"
              mode="multiple"
              showArrow={true}
              maxTagCount="responsive"
              testid="action-required-select"
              handleChange={(e: any) => {
                const optionList = filterOptionList.actionRequired;
                setSelectOptions({
                  actionRequired: e,
                });
                setFilterOptionList({
                  // @ts-ignore
                  actionRequired: [
                    ...handleChange(optionList, e),
                    ...optionList.sort((s, t) => sortList(s, t)),
                  ],
                });
              }}
              handleFilterOption={(input: any, option: any) => handleFilterOption(input, option)}
              isNeedSelectAll={filterOptionList.actionRequired.length > 0}
              handleSelectAll={() => handleSelectAll("actionRequired")}
              handleDeselectAll={() => handleDeselectAll("actionRequired")}
            />
          </div>
        )}
        {props.tab === 1 && (
          <div className={styles.topFilterSelectItem}>
            <InputLabel text="Action Taken" className={`${styles.topFilterSelectLabel} fs-14`} />
            <NormalSelect
              value={selectOptions.actionTaken}
              optionList={filterOptionList.actionTaken}
              className="topFilterSelectInput"
              mode="multiple"
              showArrow={true}
              maxTagCount="responsive"
              testid="action-taken-select"
              handleChange={(e: any) => {
                const optionList = filterOptionList.actionTaken;
                setSelectOptions({
                  actionTaken: e,
                });
                setFilterOptionList({
                  // @ts-ignore
                  actionTaken: [
                    ...handleChange(optionList, e),
                    ...optionList.sort((s, t) => sortList(s, t)),
                  ],
                });
              }}
              handleDeSelect={(e: any) => {
                const selectList = [...selectOptions.actionTaken];
                setSelectOptions({
                  actionTaken: handleDeSelect(e, selectList),
                });
              }}
              handleFilterOption={(input: any, option: any) => handleFilterOption(input, option)}
              isNeedSelectAll={filterOptionList.actionTaken.length > 0}
              handleSelectAll={() => handleSelectAll("actionTaken")}
              handleDeselectAll={() => handleDeselectAll("actionTaken")}
            />
          </div>
        )}
        <div className={styles.topFilterSelectItem}>
          <InputLabel text="Process Category" className={`${styles.topFilterSelectLabel} fs-14`} />
          <NormalSelect
            value={selectOptions.processCategory}
            optionList={filterOptionList.processCategory}
            className="topFilterSelectInput"
            mode="multiple"
            key={props.tab}
            showArrow={true}
            maxTagCount="responsive"
            testid="process-category-select"
            handleChange={(e: any) => {
              const optionList = filterOptionList.processCategory;
              // Update selected values for the category
              setSelectOptions({
                processCategory: e,
              });
              // Update filter options list with selected items first, then sorted remaining items
              setFilterOptionList({
                // @ts-ignore
                processCategory: [
                  ...handleChange(optionList, e),
                  ...optionList.sort((s, t) => sortList(s, t)),
                ],
              });
            }}
            handleDeSelect={(e: any) => {
              const selectList = [...selectOptions.processCategory];
              setSelectOptions({
                processCategory: handleDeSelect(e, selectList),
              });
            }}
            handleFilterOption={(input: any, option: any) => handleFilterOption(input, option)}
            isNeedSelectAll={filterOptionList.processCategory.length > 0}
            handleSelectAll={() => handleSelectAll("processCategory")}
            handleDeselectAll={() => handleDeselectAll("processCategory")}
          />
        </div>
      </div>
      <div className={styles.topFilterBtns}>
        <Buttons color="blue" size="middle" onClick={handleFilterClick} testid="filter-btn">
          Filter
        </Buttons>
        <Buttons color="grey" size="middle" onClick={handleClearClick} testid="clear-filter-btn">
          Clear
        </Buttons>
      </div>
    </div>
  );
}
